<script setup>
  import { useUserStore } from '@/stores/user'
  import { useMainStore } from '@/stores/main'
  import PasswordResetSentModal from '@/components/modals/PasswordResetSent.vue'
  import { ref } from 'vue'
  import User from '../../../api/User'
  import Usage from '@/components/Account/Usage.vue'
  import ContentBlock from '@/components/common/ContentBlock.vue'

  const userStore = useUserStore()
  const mainStore = useMainStore()
  const reset_running = ref(false)

  async function reset_password() {
    if (reset_running.value) return
    reset_running.value = true
    await User.request_password_reset(userStore.email)
    reset_running.value = false

    mainStore.show_modal(PasswordResetSentModal, {})
  }

  function getTypeLabel(type) {
    const typeMap = {
      1: 'Free',
      2: 'Basic',
      3: 'Professional',
      4: 'Elite',
      5: 'Enterprise',
      1337: 'Admin'
    }
    return typeMap[type] || 'Unknown'
  }

  function getTypeClass(type) {
    const classMap = {
      1: 'free',
      2: 'basic',
      3: 'professional',
      4: 'elite',
      5: 'enterprise',
      1337: 'admin'
    }
    return classMap[type] || 'unknown'
  }
</script>

<template>
  <div class="information-content">
    <!-- Usage Stats -->
    <div class="usage-section">
      <Usage/>
    </div>

    <!-- Account Information -->
    <div class="section">
      <h5 class="section-title">Account Information</h5>

      <div class="info-grid">
        <ContentBlock>
          <label class="info-label">Name</label>
          <div class="info-value">{{ userStore.name }}</div>
        </ContentBlock>

        <ContentBlock>
          <label class="info-label">Email</label>
          <div class="info-value">{{ userStore.email }}</div>
        </ContentBlock>

        <ContentBlock>
          <label class="info-label">Account Type</label>
          <div class="info-value">
            <span :class="'type-badge type-' + getTypeClass(userStore.accountType)">
              {{ getTypeLabel(userStore.accountType) }}
            </span>
            <span v-if="userStore.organizationId" class="inherited-note">
              <font-awesome-icon :icon="['fas', 'building']" /> from organization
            </span>
          </div>
        </ContentBlock>

        <ContentBlock>
          <label class="info-label">Organization</label>
          <div class="info-value" v-if="userStore.organizationId">{{ userStore.organizationName || 'Unknown' }}</div>
          <div class="info-value no-org" v-else>—</div>
        </ContentBlock>

        <ContentBlock class="password-card">
          <label class="info-label">Password</label>
          <div class="info-value">*****************</div>

          <button class="reset-btn" @click="reset_password()" :disabled="reset_running">
            <font-awesome-icon v-if="reset_running" :icon="['fas', 'spinner']" spin />
            <font-awesome-icon v-else :icon="['fas', 'arrow-rotate-left']" />
          </button>
        </ContentBlock>
      </div>
    </div>

  </div>
</template>

<style scoped>
.information-content {
  padding: 0;
}

.usage-section {
  margin-bottom: 2rem;
}

.section {
  margin-bottom: 2rem;
}

.section-title {
  color: var(--text-muted);
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

/* Password card needs the absolute-positioned reset button to anchor against
   the ContentBlock root; the default static positioning won't do. */
.password-card {
  position: relative;
}

.info-label {
  display: block;
  color: var(--text-muted);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.info-value {
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 500;
}

.reset-btn {
  position: absolute;
  right: 1rem;
  top: 1rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  padding: 0.5rem 0.75rem;
  color: var(--text-faint);
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.reset-btn:hover:not(:disabled) {
  color: var(--accent-text);
  border-color: rgba(168, 85, 247, 0.3);
  background: rgba(168, 85, 247, 0.1);
}

.reset-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.type-badge {
  padding: 0.25rem 0.75rem;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
  display: inline-block;
}

.type-free {
  background: rgba(107, 114, 128, 0.15);
  color: var(--text-muted);
}

.type-basic {
  background: rgba(59, 130, 246, 0.15);
  color: var(--color-info);
}

.type-professional {
  background: rgba(168, 85, 247, 0.15);
  color: var(--accent-text);
}

.type-elite {
  background: rgba(245, 158, 11, 0.15);
  color: var(--color-warning);
}

.type-enterprise {
  background: rgba(16, 185, 129, 0.15);
  color: var(--color-success);
}

.type-admin {
  background: rgba(239, 68, 68, 0.15);
  color: var(--color-error);
}

.inherited-note {
  display: inline-block;
  margin-left: 0.5rem;
  font-size: 0.75rem;
  color: var(--accent);
  opacity: 0.8;
}

.no-org {
  color: var(--text-faint);
  font-style: italic;
}
</style>
